// 格式信息页（type-2 纯 SSR）数据生成（nuxt#8）。逐字移植 design 的 *Format.html 脚本逻辑。
import { FORMATS } from "./formats-data.js";
import { CATEGORIES } from "./tools-data.js";
import { ftHref } from "../../utils/ftHref.js";

const TYPE_COLOR = { raster: "#ff6f9c", vector: "#16a39c", raw: "#e0a32e", animated: "#34d399", icon: "#5b8def", print: "#a78bfa", video: "#4ea2ff", audio: "#22d3ee" };

const USES = {
  raster: [["Photos & web images", "Share, post and embed pictures across sites and apps."], ["Editing & design", "Work with the format in editors like Photoshop and GIMP."], ["Storage & archiving", "Keep memories and assets in a widely-readable form."]],
  vector: [["Logos & branding", "Crisp marks that scale from favicon to billboard."], ["Print & cutting", "Vector paths drive printers, plotters and cutters."], ["Illustration & UI", "Icons and artwork that stay sharp at any size."]],
  raw: [["Professional photography", "Maximum dynamic range and editing latitude."], ["Color grading", "Recover highlights and shadows non-destructively."], ["Archival masters", "Keep the untouched original alongside exports."]],
  animated: [["Reactions & memes", "Short loops that autoplay anywhere."], ["Product demos", "Quick how-to loops in docs and emails."], ["Stickers & chat", "Lightweight animation in messengers."]],
  icon: [["App icons", "Crisp icons at every required size."], ["Favicons", "Browser-tab and bookmark icons."], ["UI assets", "Cursors, toolbars and system glyphs."]],
  print: [["Commercial printing", "Accurate ink-based color on paper."], ["Embroidery & cutting", "Machine-readable production files."], ["Document exchange", "Layouts that look identical everywhere."]],
  video: [["Sharing & social", "Clips that play on every platform."], ["Editing & post", "Footage in production pipelines."], ["Streaming & web", "Embedded and hosted playback."]],
  audio: [["Music & podcasts", "Listening on any device or app."], ["Editing & mastering", "Production-quality source audio."], ["Voice notes & clips", "Lightweight recordings to share."]],
};

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

export function getFormatSEO(want) {
  const w = String(want || "").toLowerCase();
  const F = FORMATS.find((f) => slug(f[0]) === w);
  if (!F) return null;
  const [ext, name, full, dev, year, mime, alpha, type, pop, what, open] = F;
  const cc = TYPE_COLOR[type] || "#4ea2ff";

  // 关联工具:名字里含本格式(\bEXT\b)的 convert/compress/edit 工具,最多 9 个。
  const U = ext.toUpperCase();
  const matches = [];
  (CATEGORIES || []).forEach((c) =>
    ["convert", "compress", "edit"].forEach((op) => {
      ((c.tools && c.tools[op]) || []).forEach((t) => {
        const n = (t.name || "").toUpperCase();
        if (new RegExp("\\b" + U + "\\b").test(n) && !t.soon && matches.length < 9 && !matches.some((m) => m.name === t.name)) {
          matches.push({ name: t.name, href: ftHref(t.href || ""), badge: t.badge || "", op });
        }
      });
    })
  );

  // 优劣(自动生成)。
  const lossless = /lossless/i.test(what);
  const lossy = /lossy/i.test(what) && !lossless;
  const pros = [];
  const cons = [];
  if (String(alpha).toLowerCase().startsWith("yes")) pros.push("Supports transparency");
  else cons.push("No transparency support");
  if (lossless) pros.push("Lossless — keeps full quality");
  if (lossy) { pros.push("Small file sizes"); cons.push("Lossy — discards some detail"); }
  if (/open|royalty-free/i.test(what)) pros.push("Open / royalty-free");
  if (/universal|every|widely|all browsers|most widely/i.test(what + open)) pros.push("Excellent compatibility");
  if (/not supported everywhere|isn’t supported|needs converting|convert to|older systems|legacy|obsolete|proprietary/i.test(what + open)) cons.push("Limited support — may need converting");
  if (/large files|larger|uncompressed/i.test(what)) cons.push("Large file sizes");
  if (type === "raw") { pros.push("Maximum editing latitude"); cons.push("Needs raw-capable software"); }
  if (type === "vector") pros.push("Infinitely scalable");
  if (!pros.length) pros.push("Purpose-built for its niche");
  if (!cons.length) cons.push("Less common outside its niche");

  // FAQ(自动生成)。
  const faqs = [
    [`What is a ${ext} file?`, what],
    [`How do I open a ${ext} file?`, open],
    [`Is ${ext} lossless?`, lossless ? `Yes — ${ext} preserves full quality with no compression loss.` : lossy ? `No — ${ext} uses lossy compression, trading a little detail for much smaller files.` : `${ext} is ${type === "raw" ? "an unprocessed raw container, so no detail is discarded" : "optimised for its specific use rather than strictly lossless storage"}.`],
    [`How do I convert a ${ext} file?`, `Use SharkFoto's free tools — upload your ${ext} file and convert it to a more convenient format in seconds. It's free, processed securely and never stored.`],
    [`Does ${ext} support transparency?`, String(alpha).toLowerCase().startsWith("yes") ? `Yes — ${ext} supports transparent backgrounds.` : `No — ${ext} doesn't store transparency; convert to PNG or WEBP if you need it.`],
  ];

  const related = FORMATS.filter((x) => x[7] === type && x[0] !== ext).slice(0, 10).map((x) => ({ ext: x[0], slug: slug(x[0]) }));
  const url = "https://sharkfoto.com/format/" + slug(ext) + "/";

  return {
    ext, name, full, dev, year, mime, alpha, type, cc, what, open,
    slug: slug(ext),
    title: `What is a ${ext} file? — Open, Convert & Edit ${ext} | SharkFoto`,
    desc: `${what} Learn how to open ${ext} files and convert, compress or edit them free.`,
    canonical: url, url,
    crumb: [["Home", "/"], ["Formats", "/format/"], ["." + ext, url]],
    badge: type + " format",
    h1Ext: ext,
    intro: what,
    facts: [["Full name", full], ["Developed by", dev], ["Released", year], ["Extension", "." + ext.toLowerCase()], ["MIME type", mime], ["Transparency", alpha]],
    howOpenTitle: `How to open a ${ext} file`,
    usesTitle: `What ${ext} files are used for`,
    uses: USES[type] || USES.raster,
    pros, cons,
    tools: matches,
    aiSub: `Remove the background from ${ext} images, upscale to 4K, or generate new visuals with SharkFoto AI.`,
    faqs,
    related,
  };
}
